// Enrollment-disclosure gate shared by the Refer & Save surfaces (TD-765).
//
// The ACTIVE `enrollment` disclosure must be SHOWN and explicitly ACCEPTED
// before the member submits a referral, creates a share link, replaces it, or
// activates a legacy unaccepted link. ReferPanel owns the single fetch and the
// single acceptance flag for the whole section (so accepting once covers every
// action in the panel session) and passes the state down; this file holds the
// state shape, the copy, and the checkbox so the two surfaces cannot drift.
//
// Same UX as RedeemPanel's redemption disclosure: show the text, a checkbox,
// echo `disclosure.id`; a 409 DISCLOSURE_STALE resets the acceptance and
// refetches (the member re-reads the new text); a 503
// DISCLOSURE_NOT_CONFIGURED (or a 404 on the read) means "not available yet" —
// no submit is ever offered, never a fallback to other text.

import type { ReactNode } from "react";
import { EmbedCheckbox, EmbedText } from "../../embed-shared/primitives";
import type { ActiveReferralDisclosureWire } from "./referral-api";

/** The section-owned state of the enrollment disclosure and its acceptance. */
export interface EnrollmentGateState {
  /** loading: first read in flight · ready: a revision is shown · unavailable:
   *  none active (404 on read / 503 on a POST) · error: the read failed. */
  status: "loading" | "ready" | "unavailable" | "error";
  disclosure: ActiveReferralDisclosureWire | null;
  error?: string | null;
  /** The member ticked the acceptance box for THIS revision. */
  accepted: boolean;
}

/** True only when a revision is shown and accepted — the one predicate every
 *  gated control reads. */
export function enrollmentAccepted(g: EnrollmentGateState): boolean {
  return g.status === "ready" && g.disclosure !== null && g.accepted;
}

export const ENROLLMENT_UNAVAILABLE_MESSAGE =
  "Referrals are not available yet. Please check back soon.";
export const ENROLLMENT_STALE_FALLBACK =
  "The program terms have changed. Please review the updated terms and accept them to continue.";
export const ENROLLMENT_ACCEPT_HINT =
  "Accept the Refer & Save program terms to continue.";

/** The acceptance checkbox. `id` is per call site (two surfaces can be
 *  mounted in different tabs, never together, but ids stay unique anyway). */
export function EnrollmentAcceptance({
  id,
  accepted,
  onChange,
}: {
  id: string;
  accepted: boolean;
  onChange: (accepted: boolean) => void;
}): ReactNode {
  return (
    <EmbedCheckbox
      id={id}
      checked={accepted}
      onChange={(e) => onChange(e.target.checked)}
      label={<EmbedText size="xs">I have read and agree to the Refer &amp; Save program terms.</EmbedText>}
    />
  );
}
